<script lang="ts" setup>
  import { onClickOutside } from '@vueuse/core';
  import { ElInput, ElPagination } from 'element-plus';
  import { ref, unref, watchEffect } from 'vue';
  import { IconSelectProps } from './icon-select';
  import Iconfont from './iconfont';

  defineOptions({
    name: 'IIconSelect',
  });
  const props = defineProps(IconSelectProps);
  const emit = defineEmits({
    'update:modelValue': (value: string) => value,
    change: (value: string) => value,
  });
  const iconValue = ref<string>('');
  const triggerRef = ref();
  const popoverRef = ref();

  onClickOutside(triggerRef.value, () => {
    unref(popoverRef).popperRef?.delayHide?.();
  });

  watchEffect(() => {
    iconValue.value = props.modelValue;
  });

  const searchValue = ref<string>('');
  const pageSize = 50;
  const current = ref<number>(1);
  const icons = ref<any[]>([]); // 显示列表
  const iconsBaseList = ref<any[]>(Iconfont.glyphs || []); // 基础列表
  const activeIcon = ref({
    font_class: '',
    unicode: '',
    name: '',
  });

  // 翻页
  const handleCurrentChange = (current: number) => {
    const startIndex = (current - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const list: any[] = iconsBaseList.value.slice(startIndex, endIndex);
    // 不足 pageSize 的情况，补全
    if (list.length < pageSize) {
      const len = pageSize - list.length;
      for (let i = 0; i < len; i++) {
        list.push({
          font_class: '',
          unicode: '-',
          name: '-',
        });
      }
    }
    icons.value = list;
  };

  // 首次加载第一页
  handleCurrentChange(1);

  // 点击选择
  const handleClick = (row: any) => {
    if (row.font_class) {
      activeIcon.value = row;
      iconValue.value = `icon-${row.font_class}`;
      emit('update:modelValue', iconValue.value);
      emit('change', row);
    } else {
      return null;
    }
  };

  // 搜索
  const handleChange = () => {
    if (Array.isArray(Iconfont.glyphs)) {
      if (searchValue.value) {
        iconsBaseList.value = Iconfont.glyphs.filter((item: any) => {
          return item.font_class.indexOf(searchValue.value) > -1 || item.name.indexOf(searchValue.value) > -1;
        });
        current.value = 1;
        handleCurrentChange(current.value);
      } else {
        handleHide();
      }
    }
  };

  // 弹出框显示之前触发
  function handleShow() {
    if (props.modelValue) {
      const font_class = props.modelValue.replace('icon-', '');
      const index = iconsBaseList.value.findIndex((item: any) => {
        return item.font_class === font_class;
      });
      if (index > -1) {
        activeIcon.value = iconsBaseList.value[index];
        // 该icon在第index项,找到它在第几页
        current.value = Math.ceil(index / pageSize);
        handleCurrentChange(current.value);
      }
    }
  }

  // 弹出框隐藏时触发
  // 重置检索项 searchValue
  // 重置分页 current
  function handleHide() {
    searchValue.value = '';
    activeIcon.value = {
      font_class: '',
      unicode: '',
      name: '',
    };
    current.value = 1;
    iconsBaseList.value = Iconfont.glyphs;
    handleCurrentChange(current.value);
  }
</script>

<template>
  <div>
    <div v-if="type === 'input'" class="t-icon-select__input">
      <ElInput ref="triggerRef" v-model="iconValue" placeholder="选择图标" readonly>
        <template #prepend>
          <i class="iconfont" :class="iconValue" />
        </template>
      </ElInput>
    </div>
    <div
      v-else-if="type === 'card'"
      ref="triggerRef"
      class="t-icon-select-card"
      :style="{
        width: size,
        height: size,
      }"
    >
      <template v-if="!iconValue">
        <span>选择</span>
        <span>图标</span>
      </template>
      <i v-else class="iconfont" :class="iconValue" />
    </div>
    <ElPopover
      ref="popoverRef"
      :virtual-ref="triggerRef"
      trigger="click"
      width="515px"
      @before-enter="handleShow"
      @hide="handleHide"
    >
      <div class="t-icon-select__wrap">
        <div class="icon-view">
          <div class="icon-view-box">
            <i class="iconfont" :class="`icon-${activeIcon.font_class}`" />
          </div>
          <div class="icon-text">
            <label>Name：</label>
            <span :title="activeIcon.name">{{ activeIcon.name }}</span>
          </div>
          <div class="icon-text">
            <label>Class：</label>
            <span :title="activeIcon.font_class">{{ activeIcon.font_class }}</span>
          </div>
          <div class="icon-text">
            <label>Unicode：</label>
            <span>{{ activeIcon.unicode }}</span>
          </div>
        </div>
        <div class="icon-list-wrap">
          <ElInput
            v-model="searchValue"
            class="icon-search"
            placeholder="图标Name/Class"
            clearable
            @change="handleChange"
          />
          <div class="icon-list">
            <div
              v-for="item in icons"
              :key="item.icon_id"
              class="icon-list-item"
              :class="{
                active: item.font_class === activeIcon.font_class,
                disabled: !item.font_class,
              }"
              @click="handleClick(item)"
            >
              <i class="iconfont" :class="[`icon-${item.font_class}`]" />
            </div>
          </div>
          <div class="icon-footer">
            <ElPagination
              v-model:current-page="current"
              :total="iconsBaseList.length"
              :pager-count="4"
              :page-size="pageSize"
              layout="prev,pager,next"
              background
              small
              @current-change="handleCurrentChange"
            />
          </div>
        </div>
      </div>
    </ElPopover>
  </div>
</template>
